import type {
  AdminAuctionSale,
  PatchAdminV1StoresStoreIdSalesIdBody,
  PostAdminV1StoresStoreIdSalesBody,
} from '@nucleus-commerce/api-client/admin';
import { fromZonedInput, invariant, toZonedInput } from '@nucleus-commerce/core';

import { bpsToPercent, percentToBps } from '../schemas/auction-form-schema';
import type { SaleFormInput, SaleFormOutput } from '../schemas/sale-form-schema';

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** Opens at the next whole hour, the first lot closes a week later, two minutes between lots. */
export function emptySaleForm(timeZone: string, now = Date.now()): SaleFormInput {
  const start = Math.ceil((now + 1) / HOUR) * HOUR;
  return {
    title: '',
    handle: '',
    subtitle: '',
    coverMediaId: null,
    essay: '',
    viewing: '',
    startsAt: toZonedInput(new Date(start).toISOString(), timeZone),
    firstCloseAt: toZonedInput(new Date(start + 7 * DAY).toISOString(), timeZone),
    lotIntervalMinutes: '2',
    buyerPremiumPercent: '',
  };
}

export function saleToForm(sale: AdminAuctionSale, timeZone: string): SaleFormInput {
  return {
    title: sale.title,
    handle: sale.handle,
    subtitle: sale.subtitle ?? '',
    coverMediaId: sale.coverMediaId,
    essay: sale.essay ?? '',
    viewing: sale.viewing ?? '',
    startsAt: toZonedInput(sale.startsAt, timeZone),
    firstCloseAt: toZonedInput(sale.firstCloseAt, timeZone),
    lotIntervalMinutes: String(Math.round(sale.lotIntervalSeconds / 60)),
    buyerPremiumPercent: bpsToPercent(sale.buyerPremiumBps),
  };
}

function instant(value: string, timeZone: string): string {
  const iso = fromZonedInput(value, timeZone);
  invariant(iso, 'the form schema requires a date and time');
  return iso;
}

const orNull = (value: string) => (value === '' ? null : value);

function editorial(values: SaleFormOutput) {
  return {
    title: values.title,
    subtitle: orNull(values.subtitle),
    coverMediaId: values.coverMediaId,
    essay: orNull(values.essay),
    viewing: orNull(values.viewing),
  };
}

function schedule(values: SaleFormOutput, timeZone: string) {
  return {
    startsAt: instant(values.startsAt, timeZone),
    firstCloseAt: instant(values.firstCloseAt, timeZone),
    lotIntervalSeconds: Number(values.lotIntervalMinutes) * 60,
    buyerPremiumBps:
      values.buyerPremiumPercent === '' ? null : percentToBps(values.buyerPremiumPercent),
  };
}

export function toCreateSale(
  values: SaleFormOutput,
  timeZone: string,
): PostAdminV1StoresStoreIdSalesBody {
  return { handle: values.handle, ...editorial(values), ...schedule(values, timeZone) };
}

/**
 * Editorial fields travel in every status; the handle and the schedule only while the sale is a
 * draft, since the backend answers 409 SALE_NOT_EDITABLE for them afterwards.
 */
export function toUpdateSale(
  values: SaleFormOutput,
  timeZone: string,
  scheduleEditable: boolean,
): PatchAdminV1StoresStoreIdSalesIdBody {
  return scheduleEditable
    ? { handle: values.handle, ...editorial(values), ...schedule(values, timeZone) }
    : editorial(values);
}

/** Backend field paths, prefix stripped, onto the form's fields. */
export function mapSaleField(field: string): string {
  if (field === 'lotIntervalSeconds') return 'lotIntervalMinutes';
  if (field === 'buyerPremiumBps') return 'buyerPremiumPercent';
  return field;
}
